'use client';

import * as React from 'react';
import type { CultivDash, CultivJourney } from '../types';
import { MockDefs, MockIcon } from './DashboardMock';
import './journey-mock.css';

/* Scene-2 lead-journey loop — the lead's avatar travels an n8n-style automation
   circuit: wires draw in orange, node cards light up, BEZIG→VOLTOOID badges
   flip, DAG chips and detail popovers appear along the way. 1:1 port of the
   design handoff on the standard 1220×640 .scene-canvas. Stage pills reuse
   dash.stages; "Emma"/addresses are the shared scene fixture data. */

/* lucide symbols this scene adds on top of the shared MockDefs set */
function JourneyDefs() {
  const p = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
  return (
    <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }} aria-hidden="true">
      <defs>
        <symbol id="s2d-zap" viewBox="0 0 24 24" {...p}><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></symbol>
        <symbol id="s2d-message-circle" viewBox="0 0 24 24" {...p}><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /></symbol>
        <symbol id="s2d-phone" viewBox="0 0 24 24" {...p}><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" /></symbol>
        <symbol id="s2d-git-branch" viewBox="0 0 24 24" {...p}><line x1="6" x2="6" y1="3" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></symbol>
        <symbol id="s2d-trophy" viewBox="0 0 24 24" {...p}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></symbol>
        <symbol id="s2d-circle-check" viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></symbol>
        <symbol id="s2d-banknote" viewBox="0 0 24 24" {...p}><rect width="20" height="12" x="2" y="6" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" /></symbol>
        <symbol id="s2d-clock" viewBox="0 0 24 24" {...p}><path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="10" /></symbol>
      </defs>
    </svg>
  );
}

function JIcon({ id, s = 15 }: { id: string; s?: number }) {
  return (
    <svg width={s} height={s}>
      <use href={`#s2d-${id}`} />
    </svg>
  );
}

/* the 8 grey circuit paths (drawn again in orange by the s2d-w* keyframes) */
const WIRES = [
  { d: 'M310 96 H485', kf: 's2d-w1' },
  { d: 'M735 96 H910', kf: 's2d-w2' },
  { d: 'M1160 96 H1182 Q1190 96 1190 104 V296 Q1190 304 1182 304 H1160', kf: 's2d-w3' },
  { d: 'M910 330 H778 A13 13 0 0 1 765 317 A13 13 0 0 0 752 304 H735', kf: 's2d-w4' },
  { d: 'M485 304 H310', kf: 's2d-w5' },
  { d: 'M60 304 H38 Q30 304 30 312 V504 Q30 512 38 512 H60', kf: 's2d-w6' },
  { d: 'M310 512 H485', kf: 's2d-w7' },
  { d: 'M735 512 H910', kf: 's2d-w8' },
];
const GHOST_WIRE = 'M910 350 H888 A14 14 0 0 0 874 364 V410 A14 14 0 0 1 860 424 H610';
const PORTS: Array<[number, number]> = [
  [310, 96], [485, 96], [735, 96], [910, 96], [1160, 96], [1160, 304],
  [910, 330], [910, 350], [735, 304], [485, 304], [310, 304], [60, 304],
  [60, 512], [310, 512], [485, 512], [735, 512], [910, 512],
];

export function JourneyMock({ dash, journey }: { dash: CultivDash; journey: CultivJourney }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // offsetWidth = layout width, immune to ancestor reveal-transforms (see DashboardMock)
    const fit = () => setScale(el.offsetWidth / 1220);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const j = journey;
  /* nodes 2-9: position, icon, copy, day chip + per-node keyframe indices */
  const nodes = [
    { left: 485, top: 64, icon: 'message-circle', t: j.n2t, s: j.n2s, day: 1, nk: 2, dk: 'd1' },
    { left: 910, top: 64, icon: 'phone', t: j.n3t, s: j.n3s, day: 3, nk: 3, dk: 'd3' },
    { left: 485, top: 272, icon: 'calendar', shared: true, t: j.n5t, s: j.n5s, day: 14, nk: 5, dk: 'd12' },
    { left: 60, top: 272, icon: 'file-chart', shared: true, t: j.n6t, s: j.n6s, day: 16, nk: 6, dk: 'd16' },
    { left: 60, top: 480, icon: 'trophy', t: j.n7t, s: j.n7s, day: 19, nk: 7, dk: 'd19' },
    { left: 485, top: 480, icon: 'circle-check', t: j.n8t, s: j.n8s, day: 26, nk: 8, dk: 'd26' },
    { left: 910, top: 480, icon: 'banknote', t: j.n9t, s: j.n9s, day: 27, nk: 9, dk: 'd27' },
  ];
  const pills = [
    { label: dash.stages[0], color: '#a1a1aa', won: false },
    { label: dash.stages[2], color: '#2563eb', won: false },
    { label: dash.stages[3], color: '#3e7c4f', won: true },
    { label: dash.stages[4], color: '#3e7c4f', won: true },
  ];

  const badges = (nk: number, day: number, dk: string) => (
    <>
      <span className="s2d-tag s2d-tag--busy" style={{ animation: `s2d-b${nk} 34s linear infinite both` }}>
        <JIcon id="clock" s={9} /><span className="kq-cap">{j.busy}</span>
      </span>
      <span className="s2d-tag s2d-tag--done" style={{ animation: `s2d-v${nk} 34s linear infinite both` }}>
        <JIcon id="circle-check" s={9} /><span className="kq-cap">{j.done}</span>
      </span>
      <span className="s2d-day" style={{ animation: `s2d-${dk} 34s linear infinite both` }}>
        {j.dayPrefix} {day}
      </span>
    </>
  );

  return (
    <div ref={rootRef} className="scene-canvas s2d" aria-hidden="true">
      <MockDefs />
      <JourneyDefs />
      <div className="s2d-canvas" style={{ transform: `scale(${scale})`, visibility: scale ? 'visible' : 'hidden' }}>

        {/* circuit wires: grey base + orange draw-on + connector ports */}
        <svg className="s2d-wires" width="1220" height="640" viewBox="0 0 1220 640" fill="none">
          {WIRES.map((w) => (
            <path key={w.d} d={w.d} stroke="rgba(40,40,40,.18)" strokeWidth="1.5" fill="none" />
          ))}
          <path d={GHOST_WIRE} stroke="rgba(40,40,40,.18)" strokeWidth="1.5" fill="none" strokeDasharray="5 5" />
          <g style={{ animation: 's2d-glow 34s linear infinite both' }}>
            {WIRES.map((w) => (
              <path
                key={w.d}
                className="s2d-wire"
                d={w.d}
                stroke="#E9551B"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                pathLength={100}
                style={{ animation: `${w.kf} 34s linear infinite both` }}
              />
            ))}
          </g>
        </svg>
        <svg className="s2d-ports" width="1220" height="640" viewBox="0 0 1220 640" fill="none">
          {PORTS.map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#fff" stroke="rgba(40,40,40,.3)" strokeWidth="1.5" />
          ))}
        </svg>

        {/* travelling lead avatar + stage pill */}
        <div className="s2d-av">
          <div className="s2d-av-in">
            <span className="s2d-av-dot">EJ</span>
            <span className="s2d-av-pills">
              {pills.map((pill) => (
                <span key={pill.label} className={'s2d-pill' + (pill.won ? ' s2d-pill--won' : '')}>
                  <i style={{ background: pill.color }} /><span className="kq-cap">{pill.label}</span>
                </span>
              ))}
            </span>
          </div>
        </div>

        {/* node 1 — trigger */}
        <div className="s2d-node" style={{ left: 60, top: 64, animation: 's2d-n1 34s linear infinite both' }}>
          <span className="ic"><JIcon id="zap" /></span>
          <span className="tx"><b>{j.n1t}</b><em>{j.n1s}</em></span>
          <span className="s2d-tag s2d-tag--trigger"><JIcon id="zap" s={9} /><span className="kq-cap">{j.trigger}</span></span>
        </div>

        {/* node 4 — condition (tall, branches) */}
        <div className="s2d-node s2d-node--tall" style={{ left: 910, top: 272, animation: 's2d-n4 34s linear infinite both' }}>
          <div className="s2d-node-row">
            <span className="ic"><JIcon id="git-branch" /></span>
            <span className="tx"><b>{j.n4t}</b><em>{j.n4s}</em></span>
          </div>
          <div className="s2d-branch on"><span className="kq-cap">{j.yes}</span></div>
          <div className="s2d-branch off"><span className="kq-cap">{j.no}</span></div>
          {badges(4, 9, 'd9')}
        </div>

        {/* ghost node — never visited */}
        <div className="s2d-ghost">
          <span className="ic"><JIcon id="clock" s={14} /></span>
          <span className="tx"><b>{j.ghostT}</b><em>{j.ghostS}</em></span>
        </div>

        {/* the remaining nodes */}
        {nodes.map((n) => (
          <div key={n.t} className="s2d-node" style={{ left: n.left, top: n.top, animation: `s2d-n${n.nk} 34s linear infinite both` }}>
            <span className="ic">{n.shared ? <MockIcon id={n.icon} /> : <JIcon id={n.icon} />}</span>
            <span className="tx"><b>{n.t}</b><em>{n.s}</em></span>
            {badges(n.nk, n.day, n.dk)}
          </div>
        ))}

        {/* detail popovers along the journey */}
        <div className="s2d-widgetmini">
          <div className="frame">
            <div className="head"><i /><u /></div>
            <div className="body">
              <span style={{ width: '70%' }} />
              <span className="r" />
              <span style={{ width: '60%' }} />
            </div>
          </div>
        </div>

        <div className="s2d-pop s2d-wa">
          <div className="s2d-wa-bub">
            <p>{j.waMsg}</p>
            <div className="s2d-wa-meta"><span>{j.waTime}</span><b>✓✓</b></div>
          </div>
        </div>

        <div className="s2d-pop s2d-tr">
          <div className="s2d-tr-head"><i /><b className="kq-cap">{j.transcriptTitle}</b></div>
          <div className="s2d-tr-line">{j.tr1}</div>
          <div className="s2d-tr-line">{j.tr2}</div>
          <div className="s2d-tr-tags">
            <span className="st">{j.tagStage}</span>
            <span className="nt">{j.tagNote}</span>
          </div>
        </div>

        <div className="s2d-pop s2d-reply">
          <div className="s2d-reply-k">{j.replyLabel}</div>
          <div className="s2d-reply-bub">
            <p>{j.replyMsg}</p>
            <div className="s2d-reply-t">{j.replyTime}</div>
          </div>
        </div>

        <div className="s2d-report">
          <div className="cover">
            <div className="page">
              <div className="conf">{j.repConf}</div>
              <h4>{j.repTitle}</h4>
              <div className="l1">Avenue de Mai 118, 1200 Woluwe</div>
              <div className="l2">{j.repDate}</div>
            </div>
          </div>
        </div>

        <div className="s2d-pop s2d-com">
          <div className="s2d-com-t">{j.comTitle}</div>
          <div className="s2d-com-row"><span>{j.comPrice}</span><span className="v">{j.comPriceV}</span></div>
          <div className="s2d-com-row"><span>{j.comRate}</span><span className="v">{j.comRateV}</span></div>
          <div className="s2d-com-foot">
            <b>{j.comTotal}</b>
            <span className="s2d-com-tick">
              {j.comTicks.map((amt) => <span key={amt}>{amt}</span>)}
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}
